import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { C, S } from "../customers/theme";
import { useAuth } from "../auth/AuthProvider";
import {
  listMembers, changeMemberRole, removeMember,
  listInvites, createInvite, revokeInvite, regenerateInvite, inviteMailto, buildInviteUrl,
} from "../lib/team";

let _seq = 0;
function Field({ label, value, onChange, type = "text", options, placeholder, area }) {
  const id = useMemo(() => `tm-${++_seq}`, []);
  return (
    <div>
      <label htmlFor={id} style={S.label}>{label}</label>
      {options ? (
        <select id={id} style={S.input} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
          {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
        </select>
      ) : area ? (
        <textarea id={id} style={{ ...S.input, height: 60 }} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input id={id} style={S.input} type={type} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div style={{ ...S.card, marginBottom: 16 }}>
    <div style={{ ...S.sectionTitle, marginTop: 0 }}>{title}</div>
    {children}
  </div>
);

// One-line plain-English gloss per role — neither founder nor their customers
// should have to guess what "accountant" can see.
const ROLE_OPTIONS = [
  { value: "owner", label: "Owner — full control, including billing and removing people" },
  { value: "admin", label: "Admin — manages the team and most day-to-day settings" },
  { value: "accountant", label: "Accountant — sees invoices, expenses and reports" },
  { value: "sales", label: "Sales — creates quotes, orders and invoices" },
  { value: "driver", label: "Driver — sees deliveries and routes" },
  { value: "employee", label: "Employee — general day-to-day access" },
];
const roleName = (value) => ROLE_OPTIONS.find((r) => r.value === value)?.label.split(" — ")[0] || value;

const expiryLabel = (expiresAt) => {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const days = Math.ceil(ms / 86400000);
  return days === 1 ? "expires in 1 day" : `expires in ${days} days`;
};

export default function TeamMembers() {
  const { user, companyName } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [m, i] = await Promise.all([listMembers(), listInvites()]);
      setMembers(m); setInvites(i);
    } catch (e) { setError(e.message || "Could not load the team."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, successMsg) => {
    setBusy(true); setError(""); setSaved("");
    try {
      await fn();
      await load();
      if (successMsg) { setSaved(successMsg); setTimeout(() => setSaved(""), 2500); }
    } catch (e) { setError(e.message || "Something went wrong."); }
    finally { setBusy(false); }
  };

  // Self-demotion / self-removal is blocked here in the UI only — the DB does
  // NOT prevent an owner removing or demoting themselves when a second owner
  // exists (protect_last_owner only stops the LAST owner). Disabled control +
  // a handler-level guard, since this screen is the only thing standing there.
  const onRoleChange = (m, role) => {
    if (m.user_id === user?.id) return;
    act(() => changeMemberRole(m.user_id, role), "Role updated.");
  };
  const onRemove = (m) => {
    if (m.user_id === user?.id) return;
    if (!window.confirm(`Remove ${m.full_name || m.email} from the team?`)) return;
    act(() => removeMember(m.user_id), "Member removed.");
  };

  // ── Invite someone ──────────────────────────────────────────────────────────
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("employee");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteResult, setInviteResult] = useState(null); // { email, url, expires_at }
  const [copied, setCopied] = useState(false);

  const sendInvite = async () => {
    const email = inviteEmail.trim();
    if (!email) return;
    setInviteBusy(true); setInviteError(""); setCopied(false);
    try {
      const res = await createInvite(email, inviteRole);
      setInviteResult({ email, url: buildInviteUrl(window.location.origin, res.token), expires_at: res.expires_at });
      setInviteEmail("");
      await load();
    } catch (e) { setInviteError(e.message || "Could not send the invite."); }
    finally { setInviteBusy(false); }
  };

  const writeClipboard = async (url) => {
    try { await navigator.clipboard.writeText(url); return true; }
    catch { return false; /* clipboard unavailable — the link is still visible in the field to select/copy manually */ }
  };
  const copyLink = async (url) => { if (await writeClipboard(url)) { setCopied(true); setTimeout(() => setCopied(false), 2000); } };

  // ── Invites (pending, and a short history of accepted/revoked ones) ─────────
  const [regenBusyId, setRegenBusyId] = useState(null);
  const [regenResult, setRegenResult] = useState(null); // { id, url, expires_at } — id = the invite row it belongs to
  const [copiedRegen, setCopiedRegen] = useState(false);
  const copyLinkRegen = async (url) => { if (await writeClipboard(url)) { setCopiedRegen(true); setTimeout(() => setCopiedRegen(false), 2000); } };

  const onRegenerate = async (inv) => {
    setRegenBusyId(inv.id); setError(""); setCopiedRegen(false);
    try {
      const res = await regenerateInvite(inv.id);
      // Shown INLINE in this invite's own row (below), not in the Invite-someone
      // section above — that section can be off-screen for a row further down
      // a long list, and nothing would visibly happen from the user's point of view.
      setRegenResult({ id: inv.id, url: buildInviteUrl(window.location.origin, res.token), expires_at: res.expires_at });
      await load();
    } catch (e) { setError(e.message || "Could not regenerate the link."); }
    finally { setRegenBusyId(null); }
  };
  const onRevoke = (inv) => {
    if (!window.confirm(`Revoke the invite to ${inv.email}?`)) return;
    // revoke_invite is a DELIBERATE silent no-op (it requests an end state, not a
    // thing back) — always refetch rather than optimistically marking the row
    // revoked, since a no-op looks identical to success from here.
    act(() => revokeInvite(inv.id));
  };

  return (
    <div>
      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 10, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "8px 12px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={() => setError("")}>Dismiss</button>
        </div>
      )}
      {saved && <div style={{ color: C.success, fontSize: 13, fontWeight: 600, marginBottom: 14 }}>✓ {saved}</div>}

      {/* Team */}
      <Section title="Team">
        {loading ? <div style={S.empty}>Loading…</div> : (
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Name</th><th style={S.th}>Email</th><th style={S.th}>Role</th>
              <th style={S.th}>Joined</th><th style={S.th} aria-label="Actions"></th>
            </tr></thead>
            <tbody>
              {members.length === 0 ? (
                <tr><td colSpan={5} style={S.empty}>No team members found.</td></tr>
              ) : members.map((m) => {
                const isSelf = m.user_id === user?.id;
                return (
                  <tr key={m.user_id}>
                    <td style={S.td}>
                      {isSelf ? "You" : (m.full_name || m.email)}
                      {!m.email_confirmed && (
                        <div style={{ color: C.textDim, fontSize: 11 }}>hasn't confirmed their email yet</div>
                      )}
                    </td>
                    <td style={S.td}>{m.email}</td>
                    <td style={S.td}>
                      <select style={{ ...S.input, opacity: isSelf ? 0.6 : 1 }} value={m.role} disabled={isSelf || busy}
                        onChange={(e) => onRoleChange(m, e.target.value)} aria-label={`Role for ${m.email}`}>
                        {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </td>
                    <td style={S.td}>{m.created_at ? new Date(m.created_at).toLocaleDateString("en-IE") : "—"}</td>
                    <td style={S.td}>
                      <button style={S.btn("ghost")} disabled={isSelf || busy} onClick={() => onRemove(m)} aria-label={`Remove ${m.email}`}>Remove</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Section>

      {/* Invite someone */}
      <Section title="Invite someone">
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Field label="Email" type="email" value={inviteEmail} onChange={setInviteEmail} placeholder="name@example.com" />
          </div>
          <div style={{ width: 300 }}>
            <Field label="Role" value={inviteRole} onChange={setInviteRole} options={ROLE_OPTIONS} />
          </div>
          <button style={S.btn()} onClick={sendInvite} disabled={inviteBusy || !inviteEmail.trim()}>{inviteBusy ? "Sending…" : "Send"}</button>
        </div>
        {inviteError && <div style={{ color: C.danger, fontSize: 13, marginTop: 10, fontWeight: 600 }}>{inviteError}</div>}
        {inviteResult && (
          <div style={{ marginTop: 14, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 12.5, color: C.textMuted, marginBottom: 8 }}>
              Invite link for <strong style={{ color: C.text }}>{inviteResult.email}</strong> — this is the <strong style={{ color: C.text }}>only time</strong> this link can be copied. If it's lost later, use "Regenerate link" in Invites below.
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input style={{ ...S.input, flex: 1, minWidth: 220 }} value={inviteResult.url} readOnly onFocus={(e) => e.target.select()} aria-label="Invite link" />
              <button style={S.btn("secondary")} onClick={() => copyLink(inviteResult.url)}>{copied ? "Copied ✓" : "Copy link"}</button>
              <button style={S.btn("secondary")} onClick={() => inviteMailto(inviteResult.email, inviteResult.url, companyName)}>Email invite</button>
            </div>
          </div>
        )}
      </Section>

      {/* Invites — pending ones with actions, plus a short history of accepted/revoked ones */}
      <Section title="Invites">
        {invites.length === 0 ? <div style={S.empty}>No invites sent yet.</div> : (
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Email</th><th style={S.th}>Role</th><th style={S.th}>Status</th><th style={S.th} aria-label="Actions"></th>
            </tr></thead>
            <tbody>
              {invites.map((inv) => (
                <Fragment key={inv.id}>
                  <tr>
                    <td style={S.td}>{inv.email}</td>
                    <td style={S.td}>{roleName(inv.role)}</td>
                    <td style={S.td}>{inv.status === "pending" ? expiryLabel(inv.expires_at) : inv.status}</td>
                    <td style={S.td}>
                      {inv.status === "pending" && (
                        <div style={{ display: "flex", gap: 4 }}>
                          <button style={S.btn("ghost")} disabled={regenBusyId === inv.id} onClick={() => onRegenerate(inv)} aria-label={`Regenerate link for ${inv.email}`}>
                            {regenBusyId === inv.id ? "…" : "Regenerate link"}
                          </button>
                          <button style={S.btn("ghost")} disabled={busy} onClick={() => onRevoke(inv)} aria-label={`Revoke invite for ${inv.email}`}>Revoke</button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {regenResult && regenResult.id === inv.id && (
                    <tr>
                      <td colSpan={4} style={{ ...S.td, background: C.surface2 }}>
                        <div style={{ fontSize: 12.5, color: C.textMuted, marginBottom: 8 }}>
                          New link for <strong style={{ color: C.text }}>{inv.email}</strong> — this is the <strong style={{ color: C.text }}>only time</strong> this link can be copied.
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <input style={{ ...S.input, flex: 1, minWidth: 220 }} value={regenResult.url} readOnly onFocus={(e) => e.target.select()} aria-label={`Regenerated invite link for ${inv.email}`} />
                          <button style={S.btn("secondary")} onClick={() => copyLinkRegen(regenResult.url)}>{copiedRegen ? "Copied ✓" : "Copy link"}</button>
                          <button style={S.btn("secondary")} onClick={() => inviteMailto(inv.email, regenResult.url, companyName)}>Email invite</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}
